import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserCheck, Trash2, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleDelete = async (leadId) => {
    if (!window.confirm('Es-tu sûr de vouloir supprimer ce lead ?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lead supprimé');
      fetchLeads();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExport = () => {
    const csv = 'Email,Source,Date\n' + 
      leads.map(l => `${l.email},${l.source},${new Date(l.created_at).toLocaleDateString('fr-FR')}`).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Export téléchargé');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceLabel = (source) => {
    const sources = {
      'landing': 'Landing Page',
      'final-cta': 'CTA Final',
      'hero': 'Hero Section',
      'pricing': 'Page Tarifs',
    };
    return sources[source] || source;
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-rm-text">Leads</h1>
          <p className="text-rm-text-secondary">Gérez les prospects capturés sur le site</p>
        </div>
        {leads.length > 0 && (
          <Button 
            onClick={handleExport}
            variant="outline"
            className="rounded-xl"
          >
            <Download className="w-4 h-4 mr-2" />
            Exporter CSV
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5" />
            Leads ({leads.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="spinner"></div>
            </div>
          ) : leads.length === 0 ? (
            <p className="text-rm-text-secondary text-center py-10">
              Aucun lead pour le moment
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-rm-text-secondary">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-rm-text-secondary">Source</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-rm-text-secondary">Date</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-rm-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, index) => (
                    <motion.tr
                      key={lead.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="border-b last:border-0 hover:bg-slate-50"
                    >
                      <td className="py-3 px-4">
                        <a 
                          href={`mailto:${lead.email}`}
                          className="text-rm-primary hover:underline"
                        >
                          {lead.email}
                        </a>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="secondary">{getSourceLabel(lead.source)}</Badge>
                      </td>
                      <td className="py-3 px-4 text-rm-text-secondary">{formatDate(lead.created_at)}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => handleDelete(lead.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
